import * as z from 'zod';

export const QueryServicesByFiltersRequest = z.object({
  options: z
    .object({
      query: z
        .intersection(
          z.object({
            filter: z
              .record(z.string(), z.any())
              .describe(
                'Filter object in the following format:\n`"filter" : {\n"fieldName1": "value1",\n"fieldName2":{"$operator":"value2"}\n}`\nExample of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`'
              )
              .optional()
              .nullable(),
            sort: z
              .array(
                z.object({
                  fieldName: z
                    .string()
                    .describe('Name of the field to sort by.')
                    .max(512)
                    .optional(),
                  order: z.enum(['ASC', 'DESC']).optional(),
                })
              )
              .max(5)
              .optional(),
          }),
          z.xor([
            z.object({ cursorPaging: z.never().optional() }),
            z.object({
              cursorPaging: z
                .object({
                  limit: z
                    .number()
                    .int()
                    .describe(
                      'Maximum number of items to return in the results.'
                    )
                    .min(0)
                    .max(50)
                    .optional()
                    .nullable(),
                  cursor: z
                    .string()
                    .describe(
                      "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                    )
                    .max(16000)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.'
                ),
            }),
          ])
        )
        .describe(
          'Query against the service catalog: WQL filter, sort, and cursor paging.\n\nDefault: `query.cursorPaging.limit` is `10`.'
        )
        .optional(),
      serviceFilters: z
        .object({
          localStartDate: z
            .string()
            .describe(
              'Start of the availability window (ISO local date-time, e.g. "2024-03-25T09:00:00").\nMust be provided together with `localEndDate`, and must be strictly before it.'
            )
            .optional()
            .nullable(),
          localEndDate: z
            .string()
            .describe(
              'End of the availability window (ISO local date-time, e.g. "2024-03-25T17:00:00").\nMust be provided together with `localStartDate`.\nA whole-day end may be expressed as the next day\'s "00:00:00" (exclusive) or as any time at or\nafter "23:59:00" on the same day (inclusive); both mean "through the end of that day".\nA window is treated as whole days only when it starts at "00:00:00" and ends on a day boundary;\nany other window is an hour window and is matched as such, even when it crosses midnight\n(such as "09:00" one day to "09:00" the next).'
            )
            .optional()
            .nullable(),
          timeZone: z
            .string()
            .describe(
              'IANA time zone identifier (e.g. "America/New_York") used to interpret the availability window.\nOptional; when omitted, the site\'s business time zone is used.'
            )
            .max(100)
            .optional()
            .nullable(),
          exactMatch: z
            .boolean()
            .describe(
              'Controls how strictly the availability window must be satisfied. Interpreted by window shape:\n- Sub-day hour window, true: bookable for the exact hour range, matched to the minute.\n- Sub-day hour window, false (default): at least one bookable slot within the window.\n- A single full day: bookable that day (any slot).\n- Multiple days, true: bookable on every day in the range.\n- Multiple days, false (default): at least one bookable slot anywhere in the range.\nHour-based and day-based services (those configured with a booking duration range) refine this:\na single day is matched as a whole (any slot counts), sub-day hour windows still respect `exactMatch`,\nand an exact multi-day window returns no results for hour-based services.'
            )
            .optional()
            .nullable(),
          locationIds: z.array(z.string()).max(100).optional(),
          attributes: z
            .array(
              z.intersection(
                z.object({
                  attributeId: z
                    .string()
                    .describe('ID of the attribute definition to filter by.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    enumValue: z.never().optional(),
                    numberRangeValue: z.never().optional(),
                    boolValue: z.never().optional(),
                    numberValues: z.never().optional(),
                  }),
                  z.object({
                    numberRangeValue: z.never().optional(),
                    boolValue: z.never().optional(),
                    numberValues: z.never().optional(),
                    enumValue: z
                      .string()
                      .describe(
                        "Exact enum value to match. Must be in the attribute's EnumConfig.allowed_values."
                      )
                      .max(40),
                  }),
                  z.object({
                    enumValue: z.never().optional(),
                    boolValue: z.never().optional(),
                    numberValues: z.never().optional(),
                    numberRangeValue: z
                      .object({
                        min: z
                          .number()
                          .int()
                          .describe(
                            'Minimum value (inclusive). If absent, no lower bound is applied.'
                          )
                          .optional()
                          .nullable(),
                        max: z
                          .number()
                          .int()
                          .describe(
                            'Maximum value (inclusive). If absent, no upper bound is applied.'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'Inclusive number range to match. At least one bound must be present; min must not exceed max.\nTo match an exact number, set min and max to the same value.'
                      ),
                  }),
                  z.object({
                    enumValue: z.never().optional(),
                    numberRangeValue: z.never().optional(),
                    numberValues: z.never().optional(),
                    boolValue: z
                      .boolean()
                      .describe('Exact boolean value to match.'),
                  }),
                  z.object({
                    enumValue: z.never().optional(),
                    numberRangeValue: z.never().optional(),
                    boolValue: z.never().optional(),
                    numberValues: z
                      .object({
                        values: z.array(z.number().int()).max(10).optional(),
                      })
                      .describe(
                        'Discrete set of numbers to match. At least one value must be present.\nA resource matches if its stored number equals any value in the set.'
                      ),
                  }),
                ])
              )
            )
            .max(10)
            .optional(),
          resourceTypes: z.array(z.string()).max(10).optional(),
          includeUnavailable: z
            .boolean()
            .describe(
              'When true, unavailable services are included in the response with `available` set to `false`.\nWhen absent or false, only available services are returned.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Availability and resource constraints applied on top of the service query.\nWhen absent, no availability check is performed and all pre-filtered services are returned.'
        )
        .optional(),
    })
    .optional(),
});
export const QueryServicesByFiltersResponse = z.object({
  results: z
    .array(
      z.object({
        service: z
          .object({
            _id: z
              .string()
              .describe('Service ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            appId: z
              .string()
              .describe(
                'ID of the app associated with the service. You can\'t update `appId`.\nServices are displayed in Wix Bookings only if they are associated with the Wix Bookings appId or have no associated app ID.\nDefault: `13d21c63-b5ec-5912-8397-c3a5ddb27a97` (Wix Bookings app ID)\nFor services from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`.<!-- [Scheduling Form Fields](https://dev.wix.com/docs/api-reference/business-solutions/bookings/about-scheduling-form-fields) services also use this app ID. Use `createdByAppId` to distinguish between Wix Meetings and Scheduling Form Fields services.-->\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            createdByAppId: z
              .string()
              .describe(
                "ID of the app that created the service. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during service creation by checking these sources in order:\n1. The caller's App ID from the request identity context.\n2. The service's `appId` field.\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            type: z
              .enum(['APPOINTMENT', 'CLASS', 'COURSE'])
              .describe(
                'Service type.\nLearn more about [service types](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-types).'
              )
              .optional(),
            sortOrder: z
              .number()
              .int()
              .describe(
                'Order of the service within a [category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v1/category-object).'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Service name.')
              .max(400)
              .min(1)
              .optional()
              .nullable(),
            description: z
              .string()
              .describe(
                'Service description. For example, `High-class hair styling, cuts, straightening and color`.'
              )
              .max(7000)
              .optional()
              .nullable(),
            tagLine: z
              .string()
              .describe('Short service description, such as `Hair styling`.')
              .max(6000)
              .optional()
              .nullable(),
            defaultCapacity: z
              .number()
              .int()
              .describe(
                'Default maximum number of customers that can book the service. The service cannot be booked beyond this capacity.'
              )
              .min(1)
              .max(1000)
              .optional()
              .nullable(),
            media: z
              .object({
                items: z
                  .array(
                    z.intersection(
                      z.object({}),
                      z.xor([
                        z.object({ image: z.never().optional() }),
                        z.object({
                          image: z
                            .string()
                            .describe(
                              'Details of the image associated with the service, such as URL and size.'
                            ),
                        }),
                      ])
                    )
                  )
                  .max(100)
                  .optional(),
                mainMedia: z
                  .intersection(
                    z.object({}),
                    z.xor([
                      z.object({ image: z.never().optional() }),
                      z.object({
                        image: z
                          .string()
                          .describe(
                            'Details of the image associated with the service, such as URL and size.'
                          ),
                      }),
                    ])
                  )
                  .describe('Primary media associated with the service.')
                  .optional(),
                coverMedia: z
                  .intersection(
                    z.object({}),
                    z.xor([
                      z.object({ image: z.never().optional() }),
                      z.object({
                        image: z
                          .string()
                          .describe(
                            'Details of the image associated with the service, such as URL and size.'
                          ),
                      }),
                    ])
                  )
                  .describe('Cover media associated with the service.')
                  .optional(),
              })
              .describe('Media associated with the service.')
              .optional(),
            hidden: z
              .boolean()
              .describe(
                'Whether the service is hidden from Wix Bookings pages and widgets.'
              )
              .optional()
              .nullable(),
            category: z
              .object({
                _id: z
                  .string()
                  .describe('Category ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                name: z
                  .string()
                  .describe('Category name.')
                  .max(500)
                  .optional()
                  .nullable(),
                sortOrder: z
                  .number()
                  .int()
                  .describe('Order of a category within a category list.')
                  .optional()
                  .nullable(),
              })
              .describe(
                "[Category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v2/introduction)\nthe service is associated with. Services aren't automatically assigned to a category.\nWithout an associated category, the service isn't visible on the live site."
              )
              .optional(),
            form: z
              .object({
                _id: z
                  .string()
                  .describe(
                    'ID of the form associated with the service.\nThe form information that you submit when booking includes contact details, participants, and other form fields set up for the service.\nYou can manage the service booking form fields using the Bookings Forms API.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
              })
              .describe(
                'Form the customer filled out when booking the service.'
              )
              .optional(),
            payment: z
              .intersection(
                z.object({
                  rateType: z
                    .enum([
                      'FIXED',
                      'CUSTOM',
                      'VARIED',
                      'NO_FEE',
                      'SUBSCRIPTION',
                    ])
                    .describe(
                      'The rate the customer is expected to pay for the service.'
                    )
                    .optional(),
                  options: z
                    .object({
                      online: z
                        .boolean()
                        .describe(
                          'Customers can pay for the service online.\nWhen `true`:\n+ `rateType` must be `FIXED`, `VARIED`, or `SUBSCRIPTION`.\n+ `fixed.price`, `varied.defaultPrice`, or `subscription.amountPerBillingCycle` must be specified respectively.\n\nRequired when: `rateType` is `SUBSCRIPTION`.\nRead more about [getting paid online](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online).'
                        )
                        .optional()
                        .nullable(),
                      inPerson: z
                        .boolean()
                        .describe(
                          'Customers can pay for the service in person.'
                        )
                        .optional()
                        .nullable(),
                      deposit: z
                        .boolean()
                        .describe(
                          'This service requires a deposit to be made online in order to book it.\nWhen `true`:\n+ `rateType` must be `VARIED` or `FIXED`.\n+ A `deposit` must be specified.\n+ `online` must be `true`.\n+ `inPerson` must be `false`.'
                        )
                        .optional()
                        .nullable(),
                      pricingPlan: z
                        .boolean()
                        .describe(
                          'Whether customers can pay for the service using a pricing plan.\nRead more about [service payment options](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/about-service-payments).'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The payment options a customer can use to pay for the service.'
                    )
                    .optional(),
                  pricingPlanIds: z.array(z.string()).max(75).optional(),
                  addOnOption: z
                    .enum(['ONLINE', 'IN_PERSON'])
                    .describe(
                      'How customers can pay for add-ons when paying for the related booking with a [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction).\nIf customers pay for the booking using any method other than a pricing plan, the value of this field is ignored.'
                    )
                    .optional(),
                  discountInfo: z
                    .object({
                      discountName: z
                        .string()
                        .describe(
                          'Name of the discount. For example, `Summer Sale - 20% Off`.'
                        )
                        .min(1)
                        .max(50)
                        .optional(),
                      priceAfterDiscount: z
                        .object({
                          value: z
                            .string()
                            .describe(
                              'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                            )
                            .optional(),
                          currency: z
                            .string()
                            .describe(
                              'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                            )
                            .optional(),
                          formattedValue: z
                            .string()
                            .describe(
                              'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                            )
                            .max(50)
                            .optional()
                            .nullable(),
                        })
                        .describe(
                          'Estimated price after applying the discount. The final price is determined at checkout and may differ when additional discounts are applied or rules are re-evaluated with complete booking information.\n\nNot returned when the discount depends on booking or cart context, for example a discount that applies to a service only when booked together with another service, or when the discount requires information only available at checkout.'
                        )
                        .optional(),
                    })
                    .describe(
                      "Estimated discount information for the service based on active [eCommerce discounts](https://dev.wix.com/docs/rest/business-solutions/e-commerce/extensions/discounts/introduction).\nThe final discount is determined during eCommerce checkout and may differ from the estimate,\nfor example when discounts depend on cart totals.\n\nA discount is considered active when its start time has passed and its end time hasn't.\nIf multiple active discounts apply, the most recently created one is returned.\n\nReturned only when `DISCOUNT_INFO_DETAILS` is requested."
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    fixed: z.never().optional(),
                    custom: z.never().optional(),
                    varied: z.never().optional(),
                    subscription: z.never().optional(),
                  }),
                  z.object({
                    custom: z.never().optional(),
                    varied: z.never().optional(),
                    subscription: z.never().optional(),
                    fixed: z
                      .object({
                        price: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                              )
                              .optional(),
                            formattedValue: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                              )
                              .max(50)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'The fixed price required to book the service.\n\nRequired when: `rateType` is `FIXED`'
                          )
                          .optional(),
                        deposit: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                              )
                              .optional(),
                            formattedValue: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                              )
                              .max(50)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'The deposit price required to book the service.\n\nRequired when: `rateType` is `FIXED` and `paymentOptions.deposit` is `true`'
                          )
                          .optional(),
                        fullUpfrontPaymentAllowed: z
                          .boolean()
                          .describe(
                            'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The details for the fixed price of the service.\n\nRequired when: `rateType` is `FIXED`'
                      ),
                  }),
                  z.object({
                    fixed: z.never().optional(),
                    varied: z.never().optional(),
                    subscription: z.never().optional(),
                    custom: z
                      .object({
                        description: z
                          .string()
                          .describe(
                            'A custom description explaining to the customer how to pay for the service.'
                          )
                          .max(50)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The details for the custom price of the service.\n\nRequired when: `rateType` is `CUSTOM`'
                      ),
                  }),
                  z.object({
                    fixed: z.never().optional(),
                    custom: z.never().optional(),
                    subscription: z.never().optional(),
                    varied: z
                      .object({
                        defaultPrice: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                              )
                              .optional(),
                            formattedValue: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                              )
                              .max(50)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'The default price for the service without any variants. It will also be used as the default price for any new variant.'
                          )
                          .optional(),
                        deposit: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                              )
                              .optional(),
                            formattedValue: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                              )
                              .max(50)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'The deposit price required to book the service.\n\nRequired when: `rateType` is `VARIED` and `paymentOptions.deposit` is `true`'
                          )
                          .optional(),
                        minPrice: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                              )
                              .optional(),
                            formattedValue: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                              )
                              .max(50)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'The minimal price a customer may pay for this service, based on its variants.'
                          )
                          .optional(),
                        maxPrice: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                              )
                              .optional(),
                            formattedValue: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                              )
                              .max(50)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'The maximum price a customer may pay for this service, based on its variants.'
                          )
                          .optional(),
                        fullUpfrontPaymentAllowed: z
                          .boolean()
                          .describe(
                            'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The details for the varied pricing of the service.\nRead more about [varied price options](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online#offering-varied-price-options).\n\nRequired when: `rateType` is `VARIED`'
                      ),
                  }),
                  z.object({
                    fixed: z.never().optional(),
                    custom: z.never().optional(),
                    varied: z.never().optional(),
                    subscription: z
                      .object({
                        amountPerBillingCycle: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                              )
                              .optional(),
                            formattedValue: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                              )
                              .max(50)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Base price charged per billing cycle before tax and discounts.\nThe actual amount charged may differ after checkout applies tax and discounts.'
                          )
                          .optional(),
                        frequency: z
                          .enum(['MONTHLY'])
                          .describe(
                            "Billing frequency for recurring subscription charges.\nReflected as `billingFrequency` in the booking's subscription info.\n\nCurrently supports `MONTHLY` only."
                          )
                          .optional(),
                        totalPayments: z
                          .number()
                          .int()
                          .describe(
                            "Total number of billing cycles in the subscription. For example, `12` for a 12-month subscription with monthly payments.\nReflected as `totalCycles` in the booking's subscription info.\n\nRequired when: `rateType` is `SUBSCRIPTION`."
                          )
                          .min(2)
                          .optional()
                          .nullable(),
                        firstChargeDate: z
                          .enum(['CHECKOUT', 'SCHEDULED'])
                          .describe(
                            'Determines when the first billing cycle payment is charged. Used together with `recurringStartDate` to define the subscription billing schedule.'
                          )
                          .optional(),
                        recurringStartDate: z
                          .date()
                          .describe(
                            'Date and time when recurring billing starts.\n\nWhen `firstChargeDate` is `CHECKOUT`, the first charge occurs at checkout and subsequent billing cycles start from this date.\n\nWhen `firstChargeDate` is `SCHEDULED`, the first charge occurs on this date.\n\nCustomers who book after recurring billing starts are billed for the remaining billing cycles only.\n\nRequired when: `rateType` is `SUBSCRIPTION`.'
                          )
                          .optional()
                          .nullable(),
                        enrollmentFeeAmount: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                              )
                              .optional(),
                            formattedValue: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                              )
                              .max(50)
                              .optional()
                              .nullable(),
                          })
                          .describe('Deprecated.')
                          .optional(),
                      })
                      .describe(
                        'Subscription pricing details for the service. Supported for course services only.\nThe business charges recurring monthly payments instead of a one-time fee.\n\nRequired when: `rateType` is `SUBSCRIPTION`.'
                      ),
                  }),
                ])
              )
              .describe(
                'Payment options for booking the service.\nLearn more about [service payments](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-payments).'
              )
              .optional(),
            onlineBooking: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether the service can be booked online.\nWhen set to `true`, customers can book the service online. Configure the payment options via the `service.payment` property.\nWhen set to `false`, customers cannot book the service online, and the service can only be paid for in person.'
                  )
                  .optional()
                  .nullable(),
                requireManualApproval: z
                  .boolean()
                  .describe(
                    'Booking the service requires approval by the Wix user.'
                  )
                  .optional()
                  .nullable(),
                allowMultipleRequests: z
                  .boolean()
                  .describe(
                    'Multiple customers can request to book the same time slot. This is relevant when `requireManualApproval` is `true`.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Online booking settings.')
              .optional(),
            conferencing: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    "Whether a conference link is generated for the service's sessions."
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Conferencing options for the service.')
              .optional(),
            locations: z
              .array(
                z.intersection(
                  z.object({
                    _id: z
                      .string()
                      .describe('Location ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    type: z
                      .enum(['CUSTOM', 'BUSINESS', 'CUSTOMER'])
                      .describe('Location type.\n\nDefault: `CUSTOM`')
                      .optional(),
                    calculatedAddress: z
                      .object({
                        city: z.string().optional().nullable(),
                        subdivision: z.string().optional().nullable(),
                        country: z.string().optional().nullable(),
                        postalCode: z.string().optional().nullable(),
                        addressLine1: z.string().optional().nullable(),
                      })
                      .describe(
                        'Location address. Empty for `{"type": "CUSTOMER"}`.'
                      )
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      business: z.never().optional(),
                      custom: z.never().optional(),
                    }),
                    z.object({
                      custom: z.never().optional(),
                      business: z
                        .object({
                          _id: z
                            .string()
                            .describe(
                              'ID of the business [location](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).\nWhen setting a business location, specify only the location ID. Other location details are overwritten.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          name: z
                            .string()
                            .describe('Business location name.')
                            .max(150)
                            .optional(),
                          default: z
                            .boolean()
                            .describe(
                              'Whether this is the default location. There can only be a single default location per site.'
                            )
                            .optional()
                            .nullable(),
                          address: z
                            .object({
                              city: z.string().optional().nullable(),
                              subdivision: z.string().optional().nullable(),
                              country: z.string().optional().nullable(),
                              postalCode: z.string().optional().nullable(),
                              addressLine1: z.string().optional().nullable(),
                            })
                            .describe('Business location address.')
                            .optional(),
                          email: z
                            .string()
                            .describe('Business location email.')
                            .email()
                            .optional()
                            .nullable(),
                          phone: z
                            .string()
                            .describe('Business location phone.')
                            .optional()
                            .nullable(),
                        })
                        .describe(
                          'Information about business locations. Required when `type` is `BUSINESS`.'
                        ),
                    }),
                    z.object({
                      business: z.never().optional(),
                      custom: z
                        .object({
                          _id: z
                            .string()
                            .describe('ID of the custom location.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          address: z
                            .object({
                              city: z.string().optional().nullable(),
                              subdivision: z.string().optional().nullable(),
                              country: z.string().optional().nullable(),
                              postalCode: z.string().optional().nullable(),
                              addressLine1: z.string().optional().nullable(),
                            })
                            .describe('Address of the custom location.')
                            .optional(),
                        })
                        .describe(
                          'Information about custom locations. Required when `type` is `CUSTOM`.'
                        ),
                    }),
                  ])
                )
              )
              .max(500)
              .optional(),
            bookingPolicy: z
              .object({
                _id: z
                  .string()
                  .describe('The ID to the policy for the booking.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                _createdDate: z
                  .date()
                  .describe('Date and time the policy was created.')
                  .optional()
                  .nullable(),
                _updatedDate: z
                  .date()
                  .describe('Date and time the policy was updated.')
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Name of the policy.')
                  .max(400)
                  .optional()
                  .nullable(),
                customPolicyDescription: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether the description should be displayed. If `true`, the description is displayed.\n\nDefault: `false`'
                      )
                      .optional(),
                    description: z
                      .string()
                      .describe(
                        'The description to display.\n\nDefault: Empty\nMax length: 2500 characters'
                      )
                      .max(2500)
                      .optional(),
                  })
                  .describe(
                    'Custom description for the policy. This policy is displayed to the participant.'
                  )
                  .optional(),
                default: z
                  .boolean()
                  .describe(
                    'Whether the policy is the default for the meta site.'
                  )
                  .optional()
                  .nullable(),
                limitEarlyBookingPolicy: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether there is a limit on how early a customer\ncan book. When `false`, there is no limit on the earliest\nbooking time and customers can book in advance, as early as they like.\n\nDefault: `false`'
                      )
                      .optional(),
                    earliestBookingInMinutes: z
                      .number()
                      .int()
                      .describe(
                        'Maximum number of minutes before the start of the session that a booking can be made. This value must be greater\nthan `latest_booking_in_minutes` in the `LimitLateBookingPolicy` policy.\n\nDefault: 10080 minutes (7 days)\nMin: 1 minute'
                      )
                      .min(1)
                      .optional(),
                  })
                  .describe('Policy for limiting early bookings.')
                  .optional(),
                limitLateBookingPolicy: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether there is a limit on how late a customer\ncan book. When `false`, there is no limit on the latest\nbooking time and customers can book up to the last minute.\n\nDefault: `false`'
                      )
                      .optional(),
                    latestBookingInMinutes: z
                      .number()
                      .int()
                      .describe(
                        'Minimum number of minutes before the start of the session that a booking can be made.\nFor a schedule, this is relative to the start time of the next booked session, excluding past-booked sessions.\nThis value must be less than `earliest_booking_in_minutes` in the `LimitEarlyBookingPolicy` policy.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                      )
                      .min(1)
                      .optional(),
                  })
                  .describe('Policy for limiting late bookings.')
                  .optional(),
                bookAfterStartPolicy: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether booking is allowed after the start of the schedule. When `true`,\ncustomers can book after the start of the schedule.\n\nDefault: `false`'
                      )
                      .optional(),
                  })
                  .describe(
                    'Policy on booking an entity after the start of the schedule.'
                  )
                  .optional(),
                cancellationPolicy: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether canceling a booking is allowed. When `true`, customers\ncan cancel the booking.\n\nDefault: `false`'
                      )
                      .optional(),
                    limitLatestCancellation: z
                      .boolean()
                      .describe(
                        'Whether there is a limit on the latest cancellation time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                      )
                      .optional(),
                    latestCancellationInMinutes: z
                      .number()
                      .int()
                      .describe(
                        'Minimum number of minutes before the start of the booked session that the booking can be canceled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                      )
                      .min(1)
                      .optional(),
                    allowAnonymous: z
                      .boolean()
                      .describe(
                        "Whether this cancellation policy allows anonymous cancellations.\n\n**Important**: This flag only applies when `enabled` is `true`. If the cancellation\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot cancel\nregardless of this flag's value.\n\nWhen not set (null), defaults to enabled. Anonymous cancellations are allowed by default\nso that customers aren't required to be site members to cancel their bookings.\n\nDefault: `null` (treated as `true`)"
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Policy for canceling a booked entity.')
                  .optional(),
                reschedulePolicy: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether rescheduling a booking is allowed. When `true`, customers\ncan reschedule the booking.\n\nDefault: `false`'
                      )
                      .optional(),
                    limitLatestReschedule: z
                      .boolean()
                      .describe(
                        'Whether there is a limit on the latest reschedule time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                      )
                      .optional(),
                    latestRescheduleInMinutes: z
                      .number()
                      .int()
                      .describe(
                        'Minimum number of minutes before the start of the booked session that the booking can be rescheduled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                      )
                      .min(1)
                      .optional(),
                    allowAnonymous: z
                      .boolean()
                      .describe(
                        "Whether this reschedule policy allows anonymous rescheduling.\n\n**Important**: This flag only applies when `enabled` is `true`. If the reschedule\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot reschedule\nregardless of this flag's value.\n\nWhen not set (null), defaults to enabled. Anonymous rescheduling is allowed by default\nso that customers aren't required to be site members to reschedule their bookings.\n\nDefault: `null` (treated as `true`)"
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Policy for rescheduling a booked entity.')
                  .optional(),
                waitlistPolicy: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether the session has a waitlist. If `true`, there is a waitlist.\n\nDefault: `false`'
                      )
                      .optional(),
                    capacity: z
                      .number()
                      .int()
                      .describe(
                        'Number of spots available in the waitlist.\n\nDefault: 10 spots\nMin: 1 spot'
                      )
                      .min(1)
                      .optional(),
                    reservationTimeInMinutes: z
                      .number()
                      .int()
                      .describe(
                        'Amount of time a participant is given to book, once notified that a spot is available.\n\nDefault: 10 minutes\nMin: 1 spot'
                      )
                      .min(1)
                      .optional(),
                  })
                  .describe('Waitlist policy for the service.')
                  .optional(),
                participantsPolicy: z
                  .object({
                    maxParticipantsPerBooking: z
                      .number()
                      .int()
                      .describe(
                        'Maximum number of participants allowed.\n\nDefault: 1 participant\nMin: 1 participant'
                      )
                      .min(1)
                      .optional(),
                  })
                  .describe('Policy regarding the participants per booking.')
                  .optional(),
                resourcesPolicy: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        '`true` if this policy is enabled, `false` otherwise.\nWhen `false` then the client must always select a resource when booking an appointment.'
                      )
                      .optional(),
                    autoAssignAllowed: z
                      .boolean()
                      .describe(
                        '`true`, if it is allowed to automatically assign a resource when booking an appointment,\n`false`, if the client must always select a resource.\n\nDefault: `false`'
                      )
                      .optional(),
                  })
                  .describe('Policy for allocating resources.')
                  .optional(),
                cancellationFeePolicy: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether canceling a booking will result in a cancellation fee\n\nDefault: `false`'
                      )
                      .optional(),
                    cancellationWindows: z
                      .array(
                        z.intersection(
                          z.object({
                            startInMinutes: z
                              .number()
                              .int()
                              .describe(
                                'The fee will be applied if the booked session starts within this start time in minutes.'
                              )
                              .min(1)
                              .optional()
                              .nullable(),
                          }),
                          z.xor([
                            z.object({
                              amount: z.never().optional(),
                              percentage: z.never().optional(),
                            }),
                            z.object({
                              percentage: z.never().optional(),
                              amount: z
                                .object({
                                  value: z
                                    .string()
                                    .describe(
                                      'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                                    )
                                    .optional(),
                                  currency: z
                                    .string()
                                    .describe(
                                      'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                                    )
                                    .optional(),
                                  formattedValue: z
                                    .string()
                                    .describe(
                                      'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                                    )
                                    .max(50)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Amount to be charged as a cancellation fee.'
                                ),
                            }),
                            z.object({
                              amount: z.never().optional(),
                              percentage: z
                                .string()
                                .describe(
                                  'Percentage of the original price to be charged as a cancellation fee.'
                                ),
                            }),
                          ])
                        )
                      )
                      .max(2)
                      .optional(),
                    autoCollectFeeEnabled: z
                      .boolean()
                      .describe(
                        'Whether the cancellation fee should not be automatically collected when customer cancels the booking.\n\nDefault: `true`'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Rules for cancellation fees.')
                  .optional(),
                saveCreditCardPolicy: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe('Default: `false`')
                      .optional(),
                  })
                  .describe('Rule for saving credit card.')
                  .optional(),
              })
              .describe(
                '[Policy](https://dev.wix.com/docs/rest/business-solutions/bookings/policies/booking-policies/introduction)\ndetermining under what conditions this service can be booked. For example, whether the service can only be booked up to 30 minutes before it begins.'
              )
              .optional(),
            schedule: z
              .object({
                _id: z
                  .string()
                  .describe(
                    "ID of the [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)\nto which the service's events belong."
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                firstSessionStart: z
                  .date()
                  .describe(
                    'Start time of the first session in the schedule. For courses only.'
                  )
                  .optional()
                  .nullable(),
                lastSessionEnd: z
                  .date()
                  .describe(
                    'End time of the last session in the schedule. For courses only.'
                  )
                  .optional()
                  .nullable(),
                availabilityConstraints: z
                  .object({
                    durations: z
                      .array(
                        z.object({
                          minutes: z
                            .number()
                            .int()
                            .describe('The duration of the service in minutes.')
                            .min(1)
                            .max(44639)
                            .optional(),
                        })
                      )
                      .max(50)
                      .optional(),
                    sessionDurations: z
                      .array(z.number().int())
                      .max(50)
                      .optional(),
                    timeBetweenSessions: z
                      .number()
                      .int()
                      .describe(
                        'The number of minutes between the end of a session and the start of the next.'
                      )
                      .min(0)
                      .max(720)
                      .optional(),
                  })
                  .describe('Limitations affecting the service availability.')
                  .optional(),
              })
              .describe(
                "The service's [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction),\nwhich can be used to manage the service's [events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction)."
              )
              .optional(),
            staffMemberIds: z.array(z.string()).max(220).optional(),
            staffMemberDetails: z
              .object({
                staffMembers: z
                  .array(
                    z.object({
                      staffMemberId: z
                        .string()
                        .describe(
                          "ID of the [resource](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resources-v2/introduction) associated with the staff member providing the service.\nDespite the field name, this is the resource ID, not the staff member ID.\nThis value matches the staff member's `resourceId` from the [Staff Members API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-members/introduction)\nand corresponds to the IDs in the service's `staffMemberIds` field."
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      name: z
                        .string()
                        .describe('Name of the staff member')
                        .max(40)
                        .optional()
                        .nullable(),
                      mainMedia: z
                        .intersection(
                          z.object({}),
                          z.xor([
                            z.object({ image: z.never().optional() }),
                            z.object({
                              image: z
                                .string()
                                .describe(
                                  'Details of the image associated with the staff, such as URL and size.'
                                ),
                            }),
                          ])
                        )
                        .describe('Main media associated with the service.')
                        .optional(),
                    })
                  )
                  .max(220)
                  .optional(),
              })
              .describe(
                'Staff members details. Returned only if `STAFF_MEMBER_DETAILS` conditional field was specified.'
              )
              .optional(),
            serviceResources: z
              .array(
                z.intersection(
                  z.object({
                    resourceType: z
                      .object({
                        _id: z
                          .string()
                          .describe('The type of the resource.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('The name of the resource type.')
                          .max(40)
                          .min(1)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'Details about the required [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                      )
                      .optional(),
                  }),
                  z.xor([
                    z.object({ resourceIds: z.never().optional() }),
                    z.object({
                      resourceIds: z
                        .object({
                          values: z.array(z.string()).max(100).optional(),
                        })
                        .describe(
                          'IDs of specific [resources](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resources-v2/introduction)\nassigned to this service resource. Each ID must reference a resource within the specified `resourceType`.\n\nWhen set, only these resources are considered for availability and booking.\nWhen not set, all resources of the specified resource type are eligible.'
                        ),
                    }),
                  ])
                )
              )
              .max(3)
              .optional(),
            supportedSlugs: z
              .array(
                z.object({
                  name: z
                    .string()
                    .describe(
                      "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
                    )
                    .max(500)
                    .optional(),
                  custom: z
                    .boolean()
                    .describe(
                      'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
                    )
                    .optional()
                    .nullable(),
                  _createdDate: z
                    .date()
                    .describe(
                      'Date and time the slug was created. This is a system field.'
                    )
                    .optional()
                    .nullable(),
                })
              )
              .max(100)
              .optional(),
            mainSlug: z
              .object({
                name: z
                  .string()
                  .describe(
                    "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
                  )
                  .max(500)
                  .optional(),
                custom: z
                  .boolean()
                  .describe(
                    'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
                  )
                  .optional()
                  .nullable(),
                _createdDate: z
                  .date()
                  .describe(
                    'Date and time the slug was created. This is a system field.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'Active slug for the service.\nLearn more about [service slugs](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-slugs).'
              )
              .optional(),
            urls: z
              .object({
                servicePage: z
                  .string()
                  .describe('The URL for the service page.')
                  .optional(),
                bookingPage: z
                  .string()
                  .describe(
                    'The URL for the booking entry point. It can be either to the calendar or to the service page.'
                  )
                  .optional(),
                calendarPage: z
                  .string()
                  .describe(
                    'The URL for the calendar. Can be empty if no calendar exists.'
                  )
                  .optional(),
              })
              .describe(
                'URLs to various service-related pages, such as the calendar page and the booking page.'
              )
              .optional(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe(
                'Extensions enabling users to save custom data related to the service.'
              )
              .optional(),
            seoData: z
              .object({
                tags: z
                  .array(
                    z.object({
                      type: z
                        .string()
                        .describe(
                          'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                        )
                        .optional(),
                      props: z
                        .record(z.string(), z.any())
                        .describe(
                          'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                        )
                        .optional()
                        .nullable(),
                      meta: z
                        .record(z.string(), z.any())
                        .describe(
                          'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                        )
                        .optional()
                        .nullable(),
                      children: z
                        .string()
                        .describe(
                          'SEO tag inner content. For example, `<title> inner content </title>`.'
                        )
                        .optional(),
                      custom: z
                        .boolean()
                        .describe(
                          'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                        )
                        .optional(),
                      disabled: z
                        .boolean()
                        .describe(
                          "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                        )
                        .optional(),
                    })
                  )
                  .optional(),
                settings: z
                  .object({
                    preventAutoRedirect: z
                      .boolean()
                      .describe(
                        'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                      )
                      .optional(),
                    keywords: z
                      .array(
                        z.object({
                          term: z
                            .string()
                            .describe('Keyword value.')
                            .optional(),
                          isMain: z
                            .boolean()
                            .describe(
                              'Whether the keyword is the main focus keyword.'
                            )
                            .optional(),
                          origin: z
                            .string()
                            .describe(
                              'The source that added the keyword terms to the SEO settings.'
                            )
                            .max(1000)
                            .optional()
                            .nullable(),
                        })
                      )
                      .max(5)
                      .optional(),
                  })
                  .describe('SEO general settings.')
                  .optional(),
              })
              .describe('Custom SEO data for the service.')
              .optional(),
            _createdDate: z
              .date()
              .describe(
                'Date and time the service was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
              )
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe(
                'Date and time the service was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the service is updated. To\nprevent conflicting changes, the existing revision must be used when updating\na service.'
              )
              .optional()
              .nullable(),
            addOnGroups: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe(
                      'ID of the add-on group.\nWix Bookings automatically populates this field when creating or updating an add-on group.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Name of the add-on group.')
                    .max(100)
                    .optional()
                    .nullable(),
                  maxNumberOfAddOns: z
                    .number()
                    .int()
                    .describe(
                      "Maximum number of different [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction) from the group customers can add when booking the service.\nWhen empty, there's no upper limit."
                    )
                    .optional()
                    .nullable(),
                  addOnIds: z.array(z.string()).max(7).optional(),
                  prompt: z
                    .string()
                    .describe(
                      "Description or instructional prompt of the add-on group that's displayed to customers when booking the service."
                    )
                    .max(200)
                    .optional()
                    .nullable(),
                })
              )
              .max(3)
              .optional(),
            addOnDetails: z
              .array(
                z.object({
                  addOnId: z
                    .string()
                    .describe('ID of the add-on.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  durationInMinutes: z
                    .number()
                    .int()
                    .describe(
                      'Duration in minutes for add-ons that extend service time.\nEmpty for [quantity-based add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction#terminology).'
                    )
                    .optional()
                    .nullable(),
                })
              )
              .max(50)
              .optional(),
            taxableAddress: z
              .object({
                taxableAddressType: z
                  .enum(['BUSINESS', 'BILLING'])
                  .describe('Taxable address type.')
                  .optional(),
              })
              .describe('Taxable address used to calculate tax')
              .optional(),
            primaryResourceType: z
              .string()
              .describe(
                'ID of the [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction)\nthat serves as the primary resource for this appointment service. The primary resource type\ndetermines which resources drive slot assignment and availability resolution.\n\nFor example, when set to a "Meeting Rooms" resource type, availability is calculated\nbased on the rooms\' schedules rather than staff schedules.\n\nDefault: The staff resource type.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
          })
          .describe('The service entity.')
          .optional(),
        available: z
          .boolean()
          .describe(
            'Whether the service has available slots in the requested window.\nAlways true when no date range was provided.'
          )
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe(
      "Paging metadata. The cursor in `pagingMetadata.cursors.next` is an opaque token owned by CatalogSearch.\nPass it unchanged in the next request's `query.cursorPaging.cursor`.\nDo not assume any relationship between this cursor and the internal cursors of services-2 or other upstream services."
    )
    .optional(),
});
